import { UwProduct, UwRate } from 'types/uwService';
import { ProductRatesForm } from 'store/modules/override-rates/productRatesForm';
import { action, makeObservable, observable, computed } from 'mobx';
import { RatesOverride } from 'types/productServiceV2';

export interface OverrideRatesUwProduct extends UwProduct {
  portfolioId: string;
  productId: string;
  bcIndex: number;
}

interface Props {
  uwProduct: OverrideRatesUwProduct;
  ratesType: 'original' | 'final';
}

interface OriginalRates {
  original: UwRate[];
  final: UwRate[];
}

export class UwProductRatesForm extends ProductRatesForm {
  private _selectedRateSource: 'original' | 'final' = 'final';

  // Extended property, because here there might be two kind of rates.
  // Effectively this class doesn't use the initialRates property, but it's kept here for the sake of consistency.
  private readonly _originalRates: OriginalRates = {
    original: [],
    final: [],
  };

  get ratesBySelectedSource(): UwRate[] {
    return this._selectedRateSource === 'original' ? this._originalRates.original : this._originalRates.final;
  }

  // Overrides the original method to the the selected rates source
  get ratesOverrideData(): RatesOverride {
    return {
      portfolioId: this._ratesDomainData.portfolioId,
      productId: this._ratesDomainData.productId,
      bcIndex: this._ratesDomainData.bcIndex,
      rates: this.ratesBySelectedSource.map((rate) => ({
        ...rate,
        value: Number(this._formController.fields[rate.key].value),
      })),
    };
  }

  public setRateSource(source: 'original' | 'final'): void {
    this._selectedRateSource = source;
  }

  public resetToInitialRates(): void {
    Object.keys(this._formController.fields).forEach((key) => {
      this._formController.fields[key].value = Number(
        this.ratesBySelectedSource.find((rate) => rate.key === key)?.value || '',
      ).toFixed(2);
    });
  }

  constructor(props: Props) {
    super({
      rates: props.uwProduct.original.rates,
      portfolioId: props.uwProduct.portfolioId,
      productId: props.uwProduct.productId,
      bcIndex: props.uwProduct.bcIndex,
      productName: props.uwProduct.name,
    });
    this._originalRates = {
      original: props.uwProduct.original.rates,
      final: props.uwProduct.final.rates,
    };
    this._selectedRateSource = props.ratesType;
    this._formController = UwProductRatesForm.buildFormController(this.ratesBySelectedSource);

    makeObservable<UwProductRatesForm, '_selectedRateSource' | '_originalRates'>(this, {
      _selectedRateSource: observable,
      _originalRates: observable,
      ratesBySelectedSource: computed,
      setRateSource: action.bound,
    });
  }
}
